<template>
    <div>
        <rkm-table
            :fields="fields.table"
            :filter="filter"
            :items="form.receitasHistorico"
            thead-class="d-none"
            hide-filters
        >
            <template v-slot:filters>
                <rkm-col cols="12" class="px-0">
                    <rkm-form-input
                        placeholder="Digite para buscar"
                        v-model="filter"
                    >
                        <rkm-input-group-prepend slot="prepend">
                            <rkm-input-group-text>
                                Buscar
                            </rkm-input-group-text>
                        </rkm-input-group-prepend>

                        <rkm-input-group-append slot="append">
                            <rkm-input-group-text>
                                <rkm-form-checkbox
                                    v-model="isRemedioEmCasa"
                                    inline
                                >
                                    Remédio em Casa
                                </rkm-form-checkbox>

                                <rkm-form-radio-group
                                    :options="[
                                        { value: valueShowHistorico.LAST_SIX_MONTHS, text: 'Últimos 6 meses' },
                                        { value: valueShowHistorico.COMPLETE, text: 'Completo' }
                                    ]"
                                    v-model="showHistorico"
                                />
                            </rkm-input-group-text>
                        </rkm-input-group-append>
                    </rkm-form-input>
                </rkm-col>
            </template>

            <template v-slot:cell(dataReceita)="{ item }">
                <strong>Data da receita: </strong>
                <p>{{ item.dataReceita }}</p>
            </template>

            <template v-slot:cell(unidade)="{ item }">
                <strong>Unidade/Local de atendimento: </strong>
                <p>{{ `${item.nomeFantasia} / ${item.infraestrutura}`  }}</p>
            </template>

            <template v-slot:cell(medicoSolicitante)="{ item }">
                <strong>Médico solicitante: </strong>
                <p>{{ item.medicoSolicitante }}</p>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    :color="item.observacao ? 'info' : 'secondary'"
                    small
                    v-rkm-popover.hover.left="{
                        title:'Observação médico',
                        html:true,
                        content: item.observacao ? item.observacao
                            : 'Nenhuma observação informada.',
                    }"
                >
                    <i class="fas fa-comment-alt"></i>
                </rkm-button>

                <rkm-button
                    :disabled="!item.items.find(i => !i.estornado) && item.items.find(i => !i.receitaItemEntregaId)"
                    @click="imprimir(item)"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <template #row-details="item">
                <rkm-table
                    :fields="fields.detail"
                    :items="item.item.items"
                    :tbody-tr-class="rowClass"
                    class="table-historico"
                    hide-filters
                    hide-pagination
                >
                    <template v-slot:cell(lotes)="row">
                        <div v-for="lote in row.item.lotes" :key="lote">
                            {{ lote }}
                        </div>
                    </template>

                    <template v-slot:cell(actions)="{ item }">

                        <rkm-button
                            :color="item.observacao ? 'info' : 'secondary'"
                            small
                            v-rkm-popover.hover.left="{
                                title:'Observação receita',
                                html:true,
                                content: item.observacao ? item.observacao
                                    : 'Nenhuma observação informada.',
                            }"
                        >
                            <i class="fas fa-comment-alt"></i>
                        </rkm-button>

                        <rkm-button
                            v-if="!item.estornado || item.estornoSalvo"
                            :disabled="!!item.estornado || !item.receitaItemEntregaId"
                            :title="!!item.estornado ? 'Estornado' : 'Estornar'"
                            @click="handleEstorno(item)"
                            authorize="update-farmacia-estorno-dispensacao"
                            color="danger"
                            small
                        >
                            <rkm-icon icon="undo-alt" />
                        </rkm-button>

                        <rkm-button
                            v-else
                            title="Cancelar estorno"
                            @click="handleCancelEstorno(item)"
                            authorize="update-farmacia-estorno-dispensacao"
                            color="danger"
                            small
                        >
                            <rkm-icon icon="times" />
                        </rkm-button>
                    </template>

                    <template #afterPagination>
                        <div class="mt-2">
                            <rkm-row>
                                <rkm-col>
                                    <p class="m-0">A linha em <span class="red">vermelho</span> representa que a dispensação foi estornada.</p>
                                </rkm-col>
                            </rkm-row>
                        </div>
                    </template>
                </rkm-table>
            </template>
        </rkm-table>
    </div>
</template>

<script>
// Mixins
import { file, repository, error, request, printFarmacia } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

export default {
    created() {

        this.repositories = {
            dispensacao: this.getConectaSusRepository('farmacia.dispensacao'),
            impressao: this.getConectaSusRepository('impressao.farmacia'),
            estornoDispensacao: this.getConectaSusRepository('farmacia.estornoDispensacao'),
            balancete: this.getConectaSusRepository('almoxarifado.balancete'),
        }

        this.showHistorico = this.valueShowHistorico.LAST_SIX_MONTHS

        this.addWatcherShowHistorico()
        this.addWatcherIsRemedioEmCasa()
    },
    computed: {
        fields() {

            return  {
                table: [
                    {key: 'dataReceita', label: 'Data da receita', sortable: true },
                    {key: 'unidade', label: 'Unidade/Local de atendimento', sortable: true },
                    {key: 'medicoSolicitante', label: 'Médico solicitante', sortable: true },
                    {key: 'actions', label: 'Ações', class: 'col-min' },
                ],
                detail: [
                    {key: 'dispensadoEm', label: 'Dispensação', sortable: true },
                    {key: 'usuarioNome', label: 'Usuário', sortable: true },
                    {key: 'material.descricao', label: 'Medicamento', sortable: true },
                    {key: 'lotes', label: 'Lote', sortable: true, class:'text-center' },
                    {key: 'prescrito', label: 'Prescrito', sortable: true, class:'text-center' },
                    {key: 'entregue', label: 'Retirado', sortable: true, class:'text-center' },
                    {key: 'proximaRetirada', label: 'Retirada a partir de', sortable: true },
                    {key: 'actions', label: 'Ações', class: 'col-min text-center'},
                ]
            }
        },
        valueShowHistorico() {

            return {
                LAST_SIX_MONTHS: 0,
                COMPLETE: 1
            }
        }
    },
    data: () => ({
        isRemedioEmCasa: false,
        showHistorico: null,
        filter: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        addWatcherShowHistorico() {

            this.$watch('showHistorico', function (newValue, oldValue) {

                if (newValue != oldValue) {

                    this.getHistoricoReceitas()
                }
            })
        },
        addWatcherIsRemedioEmCasa() {

            this.$watch('isRemedioEmCasa', function () {

                this.getHistoricoReceitas()
            })
        },
        getHistoricoReceitas() {

            const params = {
                complete: this.showHistorico === this.valueShowHistorico.COMPLETE,
                programa_remedio_em_casa: this.isRemedioEmCasa
            }

            this.fetch(this.repositories.dispensacao.historicoReceitas, this.form.paciente.id, { params })
                .then(({ data }) => {

                    this.setShowDetails(data)

                    this.form.receitasHistorico = data
                })
        },
        imprimir(item) {
            if(item.id) {
                if (item.isRemedioEmCasa) {
                    this.fetch(this.repositories.impressao.receitaRemedioEmCasa, item.id)
                        .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                } else {
                    this.fetch(this.repositories.dispensacao.printReceitaDispensada, item.id)
                        .then(response => {

                            this.handlePrint(response.data)
                        })
                }
            } else {
                this.setLoading()
                this.repositories.dispensacao.printReceitaHistorico(item.idV1)
                    .then(({ data }) => {
                        this.handlePrint(data)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        handlePrint(data) {

            let agendamento = {
                operadores: Object.values(data.operadores).join(', '),
                medicoNome: data.nomeMedicoSolicitante,
                medicamentos: data.itens,
                pacienteNome: this.form.paciente.cidadao.nome,
                pacienteNomeSocial: this.form.paciente.cidadao.nomeSocial,
                prontuario: this.form.paciente.prontuario,
                dataNascimento: this.form.paciente.cidadao.dataNascimento.split("-").reverse().join("/"),
                dataAtual: this.form.dataAtual,
                dataEntrega: data.dispensadoEm,
                horaAtendimento: data.horaAtendimento,
                logotipo: this.form.cliente.logotipo,
                nomePrefeitura: this.form.cliente.nome,
                ramoAtividade: this.form.cliente.sistema.ramoAtividade.nome,
                sistemaNome: this.form.cliente.sistema.nome,
                sistemaVersao: this.form.cliente.sistema.versao,
                usuario: this.form.usuario,
                unidade: this.form.unidade,
                telefone: this.form.telefone,
            }

            this.print().dispensacao(agendamento)
        },
        download(response) {

            this.openFile(response.data)
        },
        setShowDetails(receitas){

            receitas.forEach(receita => {

                receita._showDetails = true
            });
        },
        handleEstorno(item) {

            this.setLoading()

            this.repositories.balancete.checkDate(item.dataSaida)
                .then(({ data }) => {

                    if (!data.mesFechado) {

                        this.showDeleteConfirmation(
                            'Deseja realmente estornar a dispensação?',
                            async () => item.estornado = true
                        )

                        return
                    }

                    this.showValidationNotification(
                        'A data da dispensação corresponde a um mês já finalizado. Estorno não permitido.'
                    )
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleCancelEstorno(item) {

            this.showDeleteConfirmation(
                'Deseja realmente cancelar o estorno?',
                async () => item.estornado = false
            )
        },
        rowClass(item, type) {

            if (type === 'row' && item && !!item.estornado) {

                return 'table-danger'
            }
        }
    },
    mixins: [file, repository, error, request, printFarmacia],
    props: {
        form: { type: Object, default: null }
    },
}
</script>

<style scoped>
.table-historico >>> .red { color: red; }
</style>